<template>
  <div class="relative">
    <section class="bg-b2c-grey-100 text-black uppercase text-center py-4 text-xl font-medium relative
      flex items-center justify-center" v-if="title">
        <span>{{title}}</span>
        <button @click="$emit('close')" class="absolute right-2">
          <close-icon class="hover:text-b2c-red-300 " />
        </button>
    </section>

    <section class="p-6">
      <div class="flex justify-between items-center" v-if="isShippingAddress || !billingAddress">
        <material-input class="w-1/2" :name="$t('addressForm.firstname')" :showMessage="showMessages" v-model="address.firstname" />
        <span class="text-md">*</span>
        <material-input class="w-1/2 ml-6" :name="$t('addressForm.lastname')" :showMessage="showMessages" v-model="address.lastname" />
        <span class="text-md">*</span>
      </div>

      <div class="flex justify-between items-center mt-6" v-if="showTaxNumber">
        <material-input class="w-full" :name="$t('addressForm.taxNumber')" :brackets="$t('addressForm.taxNumberBracket')"
        :showMessage="showMessages || showTaxNumberMessage" @change="checkTaxNumber"
        v-model="editedCustomer.tax_number"
        :message="showTaxNumberMessage ? taxNumberMessage : $t('input.fillField')"
        :textColor="showTaxNumberMessage ? 'text-yellow-600' : null" />
        <span class="text-md">*</span>
      </div>

      <div class="flex justify-between mt-6" v-if="isShippingAddress">
        <material-input class="w-full" :name="$t('addressForm.co')" required v-model="address.co" />
      </div>

      <div class="flex justify-between items-center mt-6">
        <material-input class="w-3/4" :name="$t('addressForm.street')" :showMessage="showMessages" v-model="address.street_name" />
        <span class="text-md">*</span>
        <material-input class="w-1/4 ml-6" :name="$t('addressForm.number')" v-model="address.street_number" />
      </div>

      <div class="flex justify-between items-center mt-6">
        <material-input class="w-1/2" :name="$t('addressForm.postcode')" :showMessage="showMessages" v-model="address.postcode" />
        <span class="text-md">*</span>
        <material-input class="w-1/2 ml-6" :name="$t('addressForm.city')" :showMessage="showMessages" v-model="address.city" />
        <span class="text-md">*</span>
      </div>

      <div class="flex justify-between items-center">
        <div class="flex items-center mt-6 w-1/2">
          <material-input class="w-full" :name="$t('addressForm.state')" :showMessage="showMessages" v-model="address.state" />
          <span class="text-md">*</span>
        </div>
        <select-country class="w-1/2 ml-6" v-model="address.country" :showMessage="showMessages" />
        <span class="text-md">*</span>
      </div>

      <div class="flex items-center mt-6 w-full">
        <material-input class="w-full" :name="$t('addressForm.phone')" :showMessage="showMessages" v-model="address.phone" />
        <span class="text-md">*</span>
      </div>
      <material-input class="mt-6" :name="$t('addressForm.note')" v-model="address.note" />

      <div class="flex justify-center mt-6" v-if="!editing">
        <button class="mx-auto w-1/3 hover:text-b2c-red-300 p-2 shadow-md rounded-md
          bg-b2c-red-300 text-white hover:bg-white"
          @click="save(address)">
            {{$t('addressForm.save')}}
        </button>
      </div>
      <div class="flex justify-center mt-6" v-else>
        <button class="mx-auto w-1/3 hover:text-b2c-red-300 p-2 shadow-md rounded-md
          bg-b2c-red-300 text-white hover:bg-white"
          @click="updateAddress(address)">
            {{$t('addressForm.save')}}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import store from '@/store/index';
import { mapGetters } from 'vuex';
import MaterialInput from '@/components/blocks/MaterialInput.vue';
import {
  checkInput, isValidTaxNumber, saveNewAddress, updateGivenAddress, TaxNumberValidation,
} from '@/services/AddressesService';
import SelectCountry from '../graphics/SelectCountry.vue';
import CloseIcon from '../graphics/CloseIcon.vue';

export default {
  props: ['givenAddress', 'givenClass', 'editing', 'isShipping', 'order', 'notShowClose', 'title'],

  data() {
    return {
      address: this.givenAddress ? this.givenAddress : {
        type: this.isShipping ? 'S' : 'B',
        company: null,
        co: '',
        firstname: '',
        lastname: '',
        street_name: '',
        street_number: '',
        postcode: '',
        city: '',
        state: '',
        country: null,
        phone: '',
        note: '',
      },

      editedCustomer: {
        firstname: '',
        lastname: '',
        tax_number: '',
      },

      showMessages: false,
      showTaxNumberMessage: false,
      taxNumberMessage: this.$t('addressForm.taxNumberMessage'),
    };
  },

  components: {
    MaterialInput,
    SelectCountry,
    CloseIcon,
  },

  mounted() {
    if (this.customer) {
      if (!this.billingAddress) {
        this.address.firstname = this.customer.firstname;
        this.address.lastname = this.customer.lastname;
      }
      this.editedCustomer.tax_number = this.customer.tax_number;
    }
  },

  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      customer: 'auth/customer',
      billingAddress: 'auth/billingAddress',
      isItaly: 'isItaly',
    }),
    isShippingAddress() {
      return this.isShipping ? this.isShipping : this.address.type === 'S';
    },

    showTaxNumber() {
      if (this.isItaly && !this.isShippingAddress && !this.billingAddress
      && (!this.address.country || this.address.country === 'IT')) {
        return true;
      }
      return false;
    },
  },

  methods: {
    save(givenAddress) {
      if (this.showTaxNumber) {
        this.saveEditedCustomer(givenAddress);
        return;
      }
      this.saveAddress(givenAddress);
    },

    async saveEditedCustomer(givenAddress) {
      if (this.editedCustomer.tax_number && isValidTaxNumber(this.editedCustomer.tax_number)) {
        this.editedCustomer.firstname = this.address.firstname;
        this.editedCustomer.lastname = this.address.lastname;
        await axios.put(`/api/customers/${this.customer.id}`, this.editedCustomer);
        this.saveAddress(givenAddress);
        return;
      }
      this.showTaxNumberMessage = true;
      this.showMessages = true;
    },

    saveAddress(givenAddress) {
      const address = givenAddress;
      address.company = `${address.firstname} ${address.lastname}`;
      if (checkInput(address)) {
        saveNewAddress(this.customer.id, address).then(({ data }) => {
          store.dispatch('auth/auth');
          this.$emit('saved');
          this.$emit('close');
          store.dispatch('addNewAddress', data);
          if (this.addressType === 'B') this.$emit('has-billing');
          if (this.order) {
            this.updateOrderAddress(data);
          }
        }).catch((error) => {
          this.checkSessionErrorNotification(error);
        });
        return;
      }
      this.showMessages = true;
    },

    checkTaxNumber() {
      this.showTaxNumberMessage = false;
      TaxNumberValidation(this.editedCustomer.tax_number).then(() => {
      }).catch((errors) => {
        const message = errors.response.data.errors.codice_fiscale[0];
        this.taxNumberMessage = message;
        this.showTaxNumberMessage = true;
      });
    },

    updateAddress(givenAddress) {
      const address = givenAddress;
      address.company = `${address.firstname} ${address.lastname}`;
      if (checkInput(address)) {
        updateGivenAddress(address).then(() => {
          store.dispatch('auth/auth');
          this.$emit('close');
        }).catch((error) => {
          this.checkSessionErrorNotification(error);
        });
        return;
      }
      this.showMessages = true;
    },

    updateOrderAddress(address) {
      const order = {
        shipping_address_id: address.id,
      };
      axios.put(`/api/orders/${this.order.order_no}/address`, order).then(() => {
        this.$emit('close');
      }).catch(() => {
        this.createNotification('error', `${this.$t('notifications.errorTryAgain')}`);
      });
    },
  },
};
</script>
